import { useEffect, useRef, useState } from 'react';

import { useGetUser } from 'Backend/users/getUser';
import { useGetUsers } from 'Backend/users/getUsers';
import Avatar from 'Components/UI_Library/Avatar';
import TextInput from 'Components/UI_Library/FormElements/TextInput';
import { useOnClickOutsideSingle } from 'Components/UI_Library/ReusableHooks/useDetectOutsideClickSingle';
import { getCurrentOnBehalfOfUserId, searchString, setCurrentOnBehalfOfUserId } from 'Lib/Utils';
import { logger } from 'utils/logger';
import classes from './OnBehalfOfUserToggle.module.css';

type Props = {
    expanded?: boolean;
    mobile?: boolean;
};

const OnBehalfOfUserToggle = (props: Props) => {
    const [showOtherUsers, setShowOtherUsers] = useState(false);
    const otherUsersPopup = useRef(null);

    const [searchValue, setSearchValue] = useState<string>('');

    const { data: userResults, isSuccess: usersLoaded, refetch: refetchUsers, isFetching: _ } = useGetUsers();
    const { data: user } = useGetUser();

    const [allUsers, setAllUsers] = useState(userResults?.data || []);

    useOnClickOutsideSingle(otherUsersPopup, () => setShowOtherUsers(false));

    const currentOnBehalfOfUser = allUsers.find((onBehalfOfUser) => onBehalfOfUser.id === getCurrentOnBehalfOfUserId());

    const handleChangeUser = async (value: string) => {
        if (value === user?.id) {
            setCurrentOnBehalfOfUserId('');
        } else {
            setCurrentOnBehalfOfUserId(value);
        }
    };

    const searchFilter = (onBehalfOfUser: { id?: string; firstName?: string; lastName?: string }) => {
        if (searchString(onBehalfOfUser.firstName || '', searchValue)) {
            return true;
        }

        if (searchString(onBehalfOfUser.lastName || '', searchValue)) {
            return true;
        }

        return false;
    };

    const getUserFullName = (userProps?: { id: string; firstName?: string; lastName?: string }) => {
        if (!userProps) {
            return '';
        }

        return userProps.id !== user?.id
            ? `${userProps.firstName || ''} ${userProps.lastName || ''}`
            : `Support User (${userProps.firstName || ''} ${userProps.lastName || ''})`;
    };

    useEffect(() => {
        if (userResults?.data && user) {
            setAllUsers([...userResults.data, user]);
        } else if (user) {
            setAllUsers([user]);
        }
    }, [userResults?.data, user]);

    return !usersLoaded ? null : (
        <div
            data-testid="on-behalf-of-user-toggle"
            className={`${classes.onBehalfOfUser} ${props.expanded ? classes.expanded : ''}`}
            onClick={() => {
                !showOtherUsers &&
                    refetchUsers({ cancelRefetch: false, throwOnError: false }) // prettier
                        .catch((error: unknown) =>
                            logger.warn("Couldn't refetch users", {
                                error,
                                component: OnBehalfOfUserToggle.name,
                            })
                        );
                setShowOtherUsers(!showOtherUsers);
            }}
        >
            <div className={`${classes.logo} ${props.mobile ? classes.mobileLogo : ''}`}>
                <span className={classes.initial}>
                    <Avatar user={currentOnBehalfOfUser || user} />
                </span>
            </div>

            <div className={`${classes.text} ${props.mobile ? classes.mobileText : ''}`}>
                <span className={classes.label}>On behalf of</span>
                <span>{getUserFullName(currentOnBehalfOfUser) || getUserFullName(user)}</span>
            </div>
            <div className={`${classes.toggle} ${props.mobile ? classes.mobileToggle : ''}`}>
                <div>
                    <div />
                    <div />
                </div>
                <div>
                    <div />
                    <div />
                </div>
            </div>
            {showOtherUsers ? (
                <div
                    className={`${classes.otherUsers} ${props.mobile ? classes.mobileOtherUsers : ''}`}
                    ref={otherUsersPopup}
                >
                    <label>Switch &quot;On behalf of&quot; users</label>
                    <div className={classes.otherUserList}>
                        <TextInput
                            small
                            value={searchValue}
                            onChange={(e: { target: { value: string } }) => setSearchValue(e.target.value)}
                            placeholder="Search user"
                            autoFocus
                        />
                        {allUsers
                            .filter(searchFilter)
                            .sort((a, b) =>
                                a.id === user?.id ? -1 : getUserFullName(a).localeCompare(getUserFullName(b))
                            )
                            .map((item) => (
                                <button
                                    type="button"
                                    onClick={() => item.id && handleChangeUser(item.id)}
                                    key={item.id}
                                    value={item.id}
                                >
                                    <span>{getUserFullName(item)}</span>
                                </button>
                            ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default OnBehalfOfUserToggle;
